import {
    U2bColumnDefinitionExternalComponent, U2bColumnPipeName,
    U2bFilterSelectOptionsRelations,
    U2bTableData
} from '@core/components/layout/table/table.types';
import { BcmUserTableSettingKey } from '@shared/models/bcm-settings-user';
import { getIsActive } from '@core/functions/get-is-active';
import { Company } from '@shared/models/company';
import { paymentTypeTranslationKeys } from '@shared/models/person';
import { FilterFieldType } from '@core/datafilter/available-filter-fields';
import { DataFilterOperator } from '@core/datafilter/filter-operator';
import { FILTER_OPERATORS_DEFAULT_SELECT, FILTER_OPERATORS_DEFAULT_STRING } from '@core/datafilter/constants';
import { BcmTenantType } from '@modules/bcm/@shared/enums/bcm-tenant-type';

export const companiesTableData: U2bTableData = {
    tableId: BcmUserTableSettingKey.Companies,
    hasServerSideFiltering: true,
    columnDefinitions: [
        {
            property: 'identNumber',
            name: 'Ident-Nr.',
            translationKey: 'company.table.identNumber',
            isActive: getIsActive(false, false, false),
            filter: {},
        },
        {
            property: 'name',
            name: 'Name der Organisation',
            translationKey: 'company.table.organizationName',
            isActive: getIsActive(),
            filter: {},
        },
        {
            property: 'form',
            name: 'Gesellschaftsform',
            translationKey: 'company.table.legalForm',
            isActive: getIsActive(),
            filter: {},
        },
        {
            property: 'street',
            name: 'Strasse',
            translationKey: 'company.table.street',
            isActive: getIsActive(),
            filter: {},
        },
        {
            property: 'postCode',
            name: 'Postleitzahl',
            translationKey: 'company.table.postalCode',
            isActive: getIsActive(),
            filter: {},
        },
        {
            property: 'city',
            name: 'Ort',
            translationKey: 'company.table.city',
            isActive: getIsActive(),
            filter: {},
        },
        {
            property: 'country',
            name: 'Land',
            translationKey: 'company.table.country',
            isActive: getIsActive(false, false, false),
            parseItem: (item: Company) => item?.country?.de,
            filter: {
                property: 'country',
                fieldType: FilterFieldType.AutoComplete,
                compareAttribute: 'country',
                operators: FILTER_OPERATORS_DEFAULT_SELECT,
                selectOptionsRelation: U2bFilterSelectOptionsRelations.Countries,
                selectOptionsFilterBy: ['de'],
                selectOptionsDisplayWith: 'de'
            }
        },
        {
            property: 'tenantRelationId',
            name: 'Beziehung',
            translationKey: 'company.table.tenantRelation',
            isActive: getIsActive(),
            externalComponent: U2bColumnDefinitionExternalComponent.TenantRelationColoredName,
            parseItem: (item: Company) => item.activeAndNextTenantRelationAssignments,
            filter: {
                property: 'tenantRelationId',
                fieldType: FilterFieldType.AutoComplete,
                compareAttribute: 'id',
                filterMultiple: true,
                operators: [
                    DataFilterOperator.Contains,
                    DataFilterOperator.NotContains,
                    DataFilterOperator.IsEmpty,
                ],
                selectOptionsRelation: U2bFilterSelectOptionsRelations.TenantRelations,
                selectOptionsFilterBy: ['name'],
                selectOptionsDisplayWith: 'name',
            }
        },
        {
            property: 'note',
            name: 'Notiz',
            translationKey: 'company.table.note',
            isActive: getIsActive(false, false, false),
            filter: {}
        },
        {
            property: 'phone',
            name: 'Rufnummer',
            translationKey: 'company.table.phone',
            isActive: getIsActive(false, false, false),
            filter: {}
        },
        {
            property: 'mail',
            name: 'E-Mail',
            translationKey: 'company.table.mail',
            isActive: getIsActive(false, false, false),
            filter: {}
        },
        {
            property: 'taxNumber',
            name: 'Steuernummer',
            translationKey: 'company.table.taxNumber',
            isActive: getIsActive(false, false, false),
            filter: {}
        },
        {
            // Separate property required: duplicate matColumnDef / FormControl keys break the table (InlandPort tenants).
            property: 'taxNumberInland',
            // sortProperty/filter target the real DB column; taxNumberInland is virtual (no such column → ORDER BY would throw).
            sortProperty: 'taxNumber',
            name: 'Steuer-Nr.',
            translationKey: 'company.table.taxNumberInland',
            isActive: getIsActive(false, false, false),
            excludeIf: tenant => tenant.productType !== BcmTenantType.InlandPort,
            parseItem: (item: Company) => item?.taxNumber,
            filter: {
                property: 'taxNumber',
            },
        },
        {
            property: 'invoicePaymentType',
            name: 'Zahlart',
            translationKey: 'company.table.paymentType',
            isActive: getIsActive(false, false, false),
            parseItem: (item: Company) => paymentTypeTranslationKeys[item.invoicePaymentType] ?? item.invoicePaymentType,
            filter: {},
        },
        {
            property: 'Kreditinstitut',
            name: 'Kreditinstitut',
            translationKey: 'company.table.bankName',
            isActive: getIsActive(false, false, false),
            filter: {},
        },
        {
            property: 'accountHolder',
            name: 'Kontoinhaber',
            translationKey: 'company.table.accountHolder',
            isActive: getIsActive(false, false, false),
            filter: {},
        },
        {
            property: 'SEPAMandatId',
            name: 'SEPA Mandat ID',
            translationKey: 'company.table.sepaMandatId',
            isActive: getIsActive(false, false, false),
            filter: {},
        },
        {
            property: 'SEPAMandatSignDate',
            name: 'SEPA Datum',
            translationKey: 'company.table.sepaDate',
            isActive: getIsActive(false, false, false),
            pipe: U2bColumnPipeName.Date,
            filter: {
                fieldType: FilterFieldType.Date
            }
        },
        {
            property: 'entryDate',
            name: 'Eintritt',
            translationKey: 'company.table.entryDate',
            isActive: getIsActive(false, false, false),
            pipe: U2bColumnPipeName.Date,
            filter: {
                fieldType: FilterFieldType.Date
            }
        },
        {
            property: 'exitDate',
            name: 'Austritt',
            translationKey: 'company.table.exitDate',
            isActive: getIsActive(false, false, false),
            pipe: U2bColumnPipeName.Date,
            filter: {
                fieldType: FilterFieldType.Date
            }
        },
        {
            property: 'invoicesByMail',
            name: 'Belege per Mail',
            translationKey: 'company.table.invoicesByMail',
            isActive: getIsActive(false, false, false),
            parseItem: (company: Company) => company.invoicesByMail ?  '✔' : '',
            filter: {},
        },
        {
            property: 'invoicesByPost',
            name: 'Belege per Post',
            translationKey: 'company.table.invoicesByPost',
            isActive: getIsActive(false, false, false),
            parseItem: (company: Company) => company.invoicesByPost ?  '✔' : '',
            filter: {},
        },
    ],
};
